import { Injectable, EventEmitter } from '@angular/core';
import {
	filter,
	take,
	switchMap,
	of,
	Observable,
	throwError,
	tap,
	catchError,
	finalize,
	firstValueFrom,
	forkJoin,
	map,
	Subject,
	takeUntil,
	timer,
	expand,
	delay,
	takeWhile,
	last,
	BehaviorSubject,
	Subscription,
	shareReplay,
} from 'rxjs';
import {
	Export,
	PluginType,
	GameData,
	AIMessage,
	LoginInfo,
	Article,
	ArticleType,
} from '@meta-press/shared-plugins';
import { NotificationHandle } from '../notification/notification-handle';
import { AIAPIResponse } from '../model/AIAPIResponse';
import { BackgroundTaskQueueService } from './background-task-queue.service';
import { AiService } from './ai/ai.service';
import { StoryService } from '../story-service/story.service';
import {
	SourceMaterialService,
	SourceMaterialState,
} from '../source-material-service/source-material.service';
import { NotificationService } from '../notification-service/notification.service';
import { SettingsService } from './settings-service/settings.service';
import { GenerationUtils } from './generation-utils/generation-utils';
import {
	GenerationRequest,
	GenerationResult,
	GenerationStep,
} from './game-generation/base/game-generation.interface';
import { Story } from '../Story';
import { BackendService } from '../backend-service/backend.service';

/**
 * Batch job status
 */
export type BatchJobStatus = 'queued' | 'processing' | 'completed' | 'failed';

/**
 * Individual batch job
 */
export interface BatchJob {
	id: string;
	gameType: PluginType;
	gameName: string;
	parameters: any; // Deprecated - kept for backward compatibility
	steps: GenerationStep[]; // Multi-step generation pipeline
	currentStepIndex: number; // Which step is currently being processed
	stepResults: Map<string, any>; // Results from completed steps, keyed by stepId
	completedSteps: string[]; // Array of completed stepIds
	status: BatchJobStatus;
	result?: Export;
	error?: string;
	createdAt: Date;
	completedAt?: Date;
	backgroundTaskIds: Set<string>; // All active background task IDs (for multi-request steps)
	selectedArticles?: Article[];
	cancellationSubject: Subject<void>; // Subject to signal cancellation of the observable chain
}

/**
 * Service for managing background batch generation of games
 */
@Injectable({
	providedIn: 'root',
})
export class BatchGenerationService {
	private batchJobs: Map<string, BatchJob> = new Map();
	private jobIdCounter = 0;

	// Event emitters for UI updates
	public onJobAdded = new EventEmitter<BatchJob>();
	public onJobCompleted = new EventEmitter<BatchJob>();
	public onJobFailed = new EventEmitter<BatchJob>();
	public onStepProgress = new EventEmitter<{
		jobId: string;
		stepId: string;
		completed: number;
		total: number;
		currentItem?: string; // e.g., topic title
	}>();

	// Smart batching properties
	private pendingRequests: Map<
		string,
		{
			requests: {
				request: GenerationRequest;
				jobId: string;
				resolve: (result: {
					jobId: string;
					result: Observable<GenerationResult>;
				}) => void;
			}[];
			timeout: any;
		}
	> = new Map();
	private readonly BATCH_WINDOW_MS = 150; // Time window to collect requests for batching

	// ===============================
	// MATERIAL SYNC STATE TRACKING
	// ===============================
	// State management for reusable vector store upload flow
	// Allows early trigger (modal ngOnInit) and later hook-in (generation flow)

	/** Current state of the material sync operation */
	private _materialSyncState$ = new BehaviorSubject<
		'idle' | 'running' | 'completed' | 'error'
	>('idle');

	/** Shared observable for the current sync operation - late subscribers join via shareReplay */
	private _materialSyncObservable$: Observable<boolean> | null = null;

	/** Subscription to the current sync operation (for cancellation) */
	private _materialSyncSubscription: Subscription | null = null;

	/** Article IDs currently being synced - used to detect if sync matches requested articles */
	private _currentSyncArticleIds: number[] = [];

	/** Progress tracking - BehaviorSubject so new subscribers get current state immediately */
	private _materialSyncProgress$ = new BehaviorSubject<{
		message: string;
		progress: number;
	}>({
		message: '',
		progress: -1,
	});

	/** Subject to signal cancellation of material sync (terminates polling loops) */
	private _materialSyncCancellation$ = new Subject<void>();

	/** Public observable for material sync state */
	get materialSyncState$(): Observable<
		'idle' | 'running' | 'completed' | 'error'
	> {
		return this._materialSyncState$.asObservable();
	}

	/** Public observable for material sync progress - emits current state immediately to new subscribers */
	get materialSyncProgress$(): Observable<{
		message: string;
		progress: number;
	}> {
		return this._materialSyncProgress$.asObservable();
	}

	constructor(
		private aiService: AiService,
		private storyService: StoryService,
		private sourceMaterialService: SourceMaterialService,
		private notificationService: NotificationService,
		private settingsService: SettingsService,
		private generationUtils: GenerationUtils,
		private backgroundTaskQueue: BackgroundTaskQueueService,
		private backendService: BackendService,
	) {}

	/**
	 * Upload articles to vector store and wait for them to be ready
	 * @param user User information
	 * @param articles Articles to upload
	 * @param progressCallback Optional progress callback
	 * @param notificationHandle Optional notification handle for progress updates
	 * @returns Observable<boolean> indicating upload and readiness completion
	 */
	uploadArticlesToVectorStore(
		user: LoginInfo,
		articles: Article[],
		progressCallback?: (progressValue: number) => void,
		notificationHandle?: NotificationHandle,
	): Observable<boolean> {
		if (!articles || articles.length === 0) {
			return of(true); // No articles to upload, return success
		}

		// Filter out image, audio, and video articles - these are never uploaded to vector store
		const uploadableArticles = articles.filter(
			(article) =>
				article.type !== ArticleType.Image &&
				article.type !== ArticleType.Audio &&
				article.type !== ArticleType.Video,
		);

		if (uploadableArticles.length === 0) {
			console.log(
				'[uploadArticlesToVectorStore] No uploadable articles after filtering',
			);
			return of(true); // No articles to upload after filtering
		}

		const allArticleIds = uploadableArticles.map((a) => a.id);

		// Update notification: Checking status
		if (notificationHandle) {
			notificationHandle.update({
				message: 'Tarkistetaan lähdemateriaalin tilaa...',
			});
		}

		// Step 1: Check status of all files
		return this.aiService.GetStoreFilesStatus(allArticleIds).pipe(
			tap((statusResponses) => {
				console.log(
					'[uploadArticlesToVectorStore] Initial status check:',
					statusResponses,
				);
			}),
			switchMap((statusResponses) => {
				// Check if all files are already in_progress or completed
				const allInProgressOrCompleted =
					allArticleIds.every(
						(id) =>
							statusResponses.findIndex(
								(status) => status.id == id,
							) > -1,
					) &&
					statusResponses.every(
						(status) =>
							status.status === 'in_progress' ||
							status.status === 'completed',
					);

				if (allInProgressOrCompleted) {
					console.log(
						'[uploadArticlesToVectorStore] All files already uploaded or in progress, skipping upload',
					);
					return of(true); // All files already uploaded or processing
				}

				// Update notification: Uploading
				if (notificationHandle) {
					notificationHandle.update({
						message: 'Lähetetään tiedostoja...',
					});
				}

				// Step 2: Upload files that need uploading
				return this.aiService
					.CheckAndUploadToOpenai(allArticleIds)
					.pipe(
						tap(() => {
							console.log(
								'[uploadArticlesToVectorStore] Upload complete, starting polling',
							);
							// Update notification: Waiting for completion
							if (notificationHandle) {
								notificationHandle.update({
									message:
										'Odotetaan latauksen valmistumista...',
								});
							}
						}),
						switchMap(() => {
							// Step 3: Poll status until all files are completed or timeout
							const startTime = Date.now();
							const maxDuration = 10000; // 10 seconds
							const pollInterval = 2000; // 2 seconds

							return timer(0, pollInterval).pipe(
								switchMap(() =>
									this.aiService.GetStoreFilesStatus(
										allArticleIds,
									),
								),
								tap((statuses) => {
									console.log(
										`[uploadArticlesToVectorStore] Polling status (${Math.floor((Date.now() - startTime) / 1000)}s):`,
										statuses,
									);
								}),
								takeWhile((statuses) => {
									const elapsed = Date.now() - startTime;

									// Check if all article IDs have a status in the current response
									const allIdsHaveStatus =
										allArticleIds.every(
											(id) =>
												statuses.findIndex(
													(status) => status.id == id,
												) > -1,
										);

									// Check if all statuses are completed
									const allStatusesCompleted = statuses.every(
										(s) => s.status === 'completed',
									);

									const allCompleted =
										allIdsHaveStatus &&
										allStatusesCompleted;
									const shouldContinue =
										!allCompleted && elapsed < maxDuration;

									console.log(
										`[uploadArticlesToVectorStore] Polling condition:`,
										{
											shouldContinue,
											allIdsHaveStatus,
											allStatusesCompleted,
											elapsed: `${elapsed}ms`,
											maxDuration: `${maxDuration}ms`,
											statusCount: statuses.length,
											articleIdCount:
												allArticleIds.length,
										},
									);

									// Continue polling while not completed AND within timeout
									return shouldContinue;
								}, true),
								map(
									(statuses) =>
										allArticleIds.every(
											(id) =>
												statuses.findIndex(
													(status) => status.id == id,
												) > -1,
										) &&
										statuses.every(
											(s) => s.status === 'completed',
										),
								),
								last(), // Take only the final result
							);
						}),
					);
			}),
			catchError((error) => {
				console.error(
					'[uploadArticlesToVectorStore] Error during upload:',
					error,
				);
				return throwError(() => error);
			}),
		);
	}

	// ===============================
	// REUSABLE MATERIAL SYNC FLOW
	// ===============================

	/**
	 * Ensure source material is synced to vector store.
	 * This is a reusable, singleton-like flow that:
	 * - Can be triggered early (e.g., modal ngOnInit) for silent background sync
	 * - Can be joined by generation flow (subscribes to existing sync if running)
	 * - Uses BehaviorSubject for progress so late subscribers get current state immediately
	 *
	 * @param user User information for upload
	 * @param articles Articles to sync to vector store
	 * @returns Observable<boolean> that emits true when sync is complete
	 */
	ensureMaterialSynced(
		user: LoginInfo,
		articles: Article[],
	): Observable<boolean> {
		const articleIds = articles.map((a) => a.id);

		console.log(
			'[ensureMaterialSynced] Called with',
			articleIds.length,
			'articles',
		);
		console.log(
			'[ensureMaterialSynced] Current state:',
			this._materialSyncState$.value,
		);
		console.log(
			'[ensureMaterialSynced] Current sync article IDs:',
			this._currentSyncArticleIds,
		);

		// Case 1: Already synced with same articles - return immediately
		if (
			this._materialSyncState$.value === 'completed' &&
			this.arraysEqual(this._currentSyncArticleIds, articleIds)
		) {
			console.log(
				'[ensureMaterialSynced] Case 1: Already completed with same articles',
			);
			return of(true);
		}

		// Case 2: Currently syncing same articles - join existing flow
		if (
			this._materialSyncState$.value === 'running' &&
			this.arraysEqual(this._currentSyncArticleIds, articleIds) &&
			this._materialSyncObservable$
		) {
			console.log('[ensureMaterialSynced] Case 2: Joining existing sync');
			return this._materialSyncObservable$;
		}

		// Case 3: Different articles or idle - start new sync
		console.log('[ensureMaterialSynced] Case 3: Starting new sync');
		this.cancelMaterialSync(); // Cancel any existing sync
		this._currentSyncArticleIds = articleIds;
		this._materialSyncState$.next('running');

		// Create the shared observable for this sync operation
		this._materialSyncObservable$ =
			this.uploadArticlesToVectorStoreWithProgress(user, articles).pipe(
				tap(() => {
					console.log(
						'[ensureMaterialSynced] Sync completed successfully',
					);
					this._materialSyncState$.next('completed');
					this.updateSyncProgress('', -1); // Clear progress on complete
				}),
				catchError((err) => {
					console.error('[ensureMaterialSynced] Sync failed:', err);
					this._materialSyncState$.next('error');
					this.updateSyncProgress(
						'Virhe materiaalin valmistelussa',
						-1,
					);
					return throwError(() => err);
				}),
				shareReplay(1), // Share result with late subscribers
			);

		// Subscribe to start the sync (make it hot)
		this._materialSyncSubscription =
			this._materialSyncObservable$.subscribe({
				error: (err) => {
					console.error(
						'[ensureMaterialSynced] Subscription error:',
						err,
					);
				},
			});

		return this._materialSyncObservable$;
	}

	/**
	 * Cancel any in-progress material sync operation.
	 * Called when source material changes or user navigates away.
	 */
	cancelMaterialSync(): void {
		console.log('[cancelMaterialSync] Cancelling material sync');

		// Signal cancellation to terminate any ongoing polling loops
		this._materialSyncCancellation$.next();

		if (this._materialSyncSubscription) {
			this._materialSyncSubscription.unsubscribe();
			this._materialSyncSubscription = null;
		}

		this._materialSyncObservable$ = null;
		this._currentSyncArticleIds = [];
		this._materialSyncState$.next('idle');
		this.updateSyncProgress('', -1); // Clear progress
	}

	/**
	 * Check if sync is needed for given articles.
	 * @param articleIds Article IDs to check
	 * @returns true if sync is needed (not completed for these articles)
	 */
	needsSync(articleIds: number[]): boolean {
		return !(
			this._materialSyncState$.value === 'completed' &&
			this.arraysEqual(this._currentSyncArticleIds, articleIds)
		);
	}

	/**
	 * Get current sync state value (for immediate checks)
	 */
	get currentSyncState(): 'idle' | 'running' | 'completed' | 'error' {
		return this._materialSyncState$.value;
	}

	/**
	 * Internal method to upload articles to vector store with progress tracking.
	 * Uses updateSyncProgress() instead of notification handle for centralized progress.
	 *
	 * @param user User information
	 * @param articles Articles to upload
	 * @returns Observable<boolean> indicating upload completion
	 */
	private uploadArticlesToVectorStoreWithProgress(
		_user: LoginInfo,
		articles: Article[],
	): Observable<boolean> {
		if (!articles || articles.length === 0) {
			return of(true); // No articles to upload, return success
		}

		// Filter out image, audio, and video articles - these are never uploaded to vector store
		const uploadableArticles = articles.filter(
			(article) =>
				article.type !== ArticleType.Image &&
				article.type !== ArticleType.Audio &&
				article.type !== ArticleType.Video,
		);

		if (uploadableArticles.length === 0) {
			console.log(
				'[uploadArticlesToVectorStoreWithProgress] No uploadable articles after filtering',
			);
			return of(true);
		}

		const allArticleIds = uploadableArticles.map((a) => a.id);

		// Update progress: Checking status
		this.updateSyncProgress('Tarkistetaan lähdemateriaalin tilaa...', -2);

		// Step 1: Check status of all files
		// Apply takeUntil at the start of the chain so cancellation stops everything
		return this.aiService.GetStoreFilesStatus(allArticleIds).pipe(
			// Cancel entire sync flow when material sync is cancelled
			takeUntil(this._materialSyncCancellation$),
			tap((statusResponses) => {
				console.log(
					'[uploadArticlesToVectorStoreWithProgress] Initial status check:',
					statusResponses,
				);
			}),
			switchMap((statusResponses) => {
				// Check if all files are already in_progress or completed
				const allInProgressOrCompleted =
					allArticleIds.every(
						(id) =>
							statusResponses.findIndex(
								(status) => status.id == id,
							) > -1,
					) &&
					statusResponses.every(
						(status) =>
							status.status === 'in_progress' ||
							status.status === 'completed',
					);

				if (allInProgressOrCompleted) {
					console.log(
						'[uploadArticlesToVectorStoreWithProgress] All files already uploaded or in progress',
					);
					return of(true);
				}

				// Update progress: Uploading
				this.updateSyncProgress('Lähetetään tiedostoja...', -2);

				// Step 2: Upload files that need uploading
				return this.aiService
					.CheckAndUploadToOpenai(allArticleIds)
					.pipe(
						// Cancel upload flow if material sync is cancelled
						takeUntil(this._materialSyncCancellation$),
						tap(() => {
							console.log(
								'[uploadArticlesToVectorStoreWithProgress] Upload complete, starting polling',
							);
							this.updateSyncProgress(
								'Odotetaan latauksen valmistumista...',
								-2,
							);
						}),
						switchMap(() => {
							// Step 3: Poll status until all files are completed or timeout
							const startTime = Date.now();
							const maxDuration = 10000; // 10 seconds
							const pollInterval = 2000; // 2 seconds

							return timer(0, pollInterval).pipe(
								// Cancel polling when material sync is cancelled
								takeUntil(this._materialSyncCancellation$),
								switchMap(() =>
									this.aiService.GetStoreFilesStatus(
										allArticleIds,
									),
								),
								tap((statuses) => {
									console.log(
										`[uploadArticlesToVectorStoreWithProgress] Polling status (${Math.floor((Date.now() - startTime) / 1000)}s):`,
										statuses,
									);
								}),
								takeWhile((statuses) => {
									const elapsed = Date.now() - startTime;
									const allIdsHaveStatus =
										allArticleIds.every(
											(id) =>
												statuses.findIndex(
													(status) => status.id == id,
												) > -1,
										);
									const allStatusesCompleted = statuses.every(
										(s) => s.status === 'completed',
									);
									const allCompleted =
										allIdsHaveStatus &&
										allStatusesCompleted;
									const shouldContinue =
										!allCompleted && elapsed < maxDuration;
									return shouldContinue;
								}, true),
								map(
									(statuses) =>
										allArticleIds.every(
											(id) =>
												statuses.findIndex(
													(status) => status.id == id,
												) > -1,
										) &&
										statuses.every(
											(s) => s.status === 'completed',
										),
								),
								last(), // Take only the final result
							);
						}),
					);
			}),
			catchError((error) => {
				console.error(
					'[uploadArticlesToVectorStoreWithProgress] Error during upload:',
					error,
				);
				return throwError(() => error);
			}),
		);
	}

	/**
	 * Update sync progress (internal method).
	 * Updates the BehaviorSubject so all subscribers get the update.
	 *
	 * @param message Progress message
	 * @param progress Progress value (-2 for spinner, -1 for no progress, 0-100 for percentage)
	 */
	private updateSyncProgress(message: string, progress: number): void {
		this._materialSyncProgress$.next({ message, progress });
	}

	/**
	 * Helper to compare two arrays for equality (order matters)
	 */
	private arraysEqual(a: number[], b: number[]): boolean {
		if (a.length !== b.length) return false;
		// Sort copies to compare regardless of order
		const sortedA = [...a].sort((x, y) => x - y);
		const sortedB = [...b].sort((x, y) => x - y);
		return sortedA.every((val, idx) => val === sortedB[idx]);
	}

	/**
	 * Queue a new batch generation job
	 * @param request Generation request
	 * @returns Job ID for tracking
	 */
	async queueGeneration(request: GenerationRequest): Promise<string> {
		// Require steps - no backward compatibility
		if (!request.steps || request.steps.length === 0) {
			throw new Error(
				'Generation request must have steps. ' +
					'Use createBackgroundGenerationRequest() or ensure steps are created before queuing.',
			);
		}

		// Validate request
		const validation = this.generationUtils.validateRequest(request);
		if (!validation.valid) {
			throw new Error(validation.error);
		}

		// Create batch job
		const jobId = `batch-${++this.jobIdCounter}`;
		const gameName = this.generateGameName(request);

		const job: BatchJob = {
			id: jobId,
			gameType: request.gameType,
			gameName,
			parameters: request.parameters, // Keep for backward compatibility
			steps: request.steps,
			currentStepIndex: 0,
			stepResults: new Map<string, any>(),
			completedSteps: [],
			status: 'queued',
			createdAt: new Date(),
			backgroundTaskIds: new Set<string>(),
			selectedArticles: request.sourceMaterial?.selectedArticles,
			cancellationSubject: new Subject<void>(),
		};

		this.batchJobs.set(jobId, job);
		this.onJobAdded.emit(job);

		// Start the generation process
		this.startBatchGeneration(jobId, request);

		return jobId;
	}

	/**
	 * Generate content synchronously with smart batching for shared source material
	 * @param request Generation request
	 * @returns Object with jobId and result observable
	 */
	generateSynchronously(request: GenerationRequest): {
		jobId: string;
		result: Observable<GenerationResult>;
	} {
		// Require steps - no backward compatibility
		if (!request.steps || request.steps.length === 0) {
			const jobId = `sync-${++this.jobIdCounter}`;
			return {
				jobId,
				result: throwError(
					() =>
						new Error(
							'Generation request must have steps. ' +
								'Use createBackgroundGenerationRequest() or ensure steps are created before queuing.',
						),
				),
			};
		}

		// Validate request
		const validation = this.generationUtils.validateRequest(request);
		if (!validation.valid) {
			const jobId = `sync-${++this.jobIdCounter}`;
			return {
				jobId,
				result: throwError(() => new Error(validation.error)),
			};
		}

		// Check if this request has source material that can be batched
		const sourceKey = this.getSourceMaterialKey(request);
		if (sourceKey) {
			console.log(
				`🔄 BATCH SERVICE: Smart batching enabled for source key: ${sourceKey}`,
			);
			return this.addToBatchQueue(request, sourceKey);
		}

		// No source material or not batchable, process immediately
		console.log(
			'🔄 BATCH SERVICE: No source material, processing immediately',
		);
		return this.processSingleRequest(request);
	}

	/**
	 * Generate a key for source material to group requests
	 * @param request Generation request
	 * @returns Source material key or null if not batchable
	 */
	private getSourceMaterialKey(request: GenerationRequest): string | null {
		const sourceMaterial = request.sourceMaterial;
		if (
			!sourceMaterial?.selectedArticles?.length ||
			!sourceMaterial?.fileHandles?.length
		) {
			return null;
		}

		// Create a key based on the file handles (sorted for consistency)
		const sortedHandles = [...sourceMaterial.fileHandles].sort();
		return `source_${sortedHandles.join('_')}`;
	}

	/**
	 * Add request to batch queue with time window
	 * @param request Generation request
	 * @param sourceKey Source material key
	 * @returns Object with jobId and result observable
	 */
	private addToBatchQueue(
		request: GenerationRequest,
		sourceKey: string,
	): { jobId: string; result: Observable<GenerationResult> } {
		const jobId = `sync-${++this.jobIdCounter}`;

		// Ensure steps are defined (should be from backward compatibility check in generateSynchronously)
		const steps = request.steps || [
			{
				stepId: 'main',
				stepType: 'generate',
				parameters: request.parameters || {},
			},
		];

		// Create placeholder BatchJob immediately for tracking and cancellation
		const placeholderJob: BatchJob = {
			id: jobId,
			gameType: request.gameType,
			gameName: this.generateGameName(request),
			parameters: request.parameters,
			steps: steps,
			currentStepIndex: 0,
			stepResults: new Map<string, any>(),
			completedSteps: [],
			status: 'queued',
			createdAt: new Date(),
			backgroundTaskIds: new Set<string>(),
			selectedArticles: request.sourceMaterial?.selectedArticles,
			cancellationSubject: new Subject<void>(),
		};

		// Add to tracked jobs and emit event
		this.batchJobs.set(jobId, placeholderJob);
		this.onJobAdded.emit(placeholderJob);
		console.log(
			`🔄 BATCH SERVICE: Created placeholder job ${jobId} for batching`,
		);

		return {
			jobId,
			result: new Observable<GenerationResult>((subscriber) => {
				// Add this request to the pending queue
				if (!this.pendingRequests.has(sourceKey)) {
					this.pendingRequests.set(sourceKey, {
						requests: [],
						timeout: null,
					});
				}

				const batchGroup = this.pendingRequests.get(sourceKey)!;

				// Add this request to the group with its jobId
				batchGroup.requests.push({
					request,
					jobId: jobId, // Store the jobId for later use
					resolve: (result) => {
						// When the request is resolved, emit to this subscriber
						result.result.subscribe({
							next: (generationResult) =>
								subscriber.next(generationResult),
							error: (error) => subscriber.error(error),
							complete: () => subscriber.complete(),
						});
					},
				});

				// Clear existing timeout and set new one
				if (batchGroup.timeout) {
					clearTimeout(batchGroup.timeout);
				}

				batchGroup.timeout = setTimeout(() => {
					this.processBatchGroup(sourceKey);
				}, this.BATCH_WINDOW_MS);

				console.log(
					`🔄 BATCH SERVICE: Added request to group ${sourceKey}, total: ${batchGroup.requests.length}`,
				);
			}),
		};
	}

	/**
	 * Process a batch group when the time window expires
	 * @param sourceKey Source material key
	 */
	private processBatchGroup(sourceKey: string): void {
		const batchGroup = this.pendingRequests.get(sourceKey);
		if (!batchGroup || batchGroup.requests.length === 0) {
			return;
		}

		console.log(
			`🔄 BATCH SERVICE: Processing batch group ${sourceKey} with ${batchGroup.requests.length} requests`,
		);

		// Remove from pending
		this.pendingRequests.delete(sourceKey);

		// Clear timeout
		if (batchGroup.timeout) {
			clearTimeout(batchGroup.timeout);
		}

		if (batchGroup.requests.length === 1) {
			// Single request, process normally
			const { request, jobId, resolve } = batchGroup.requests[0];
			const result = this.processSingleRequest(request, jobId);
			resolve(result);
		} else {
			// Multiple requests, use shared sync optimization
			this.processBatchedRequests(batchGroup.requests, sourceKey);
		}
	}

	/**
	 * Process multiple requests with shared file sync
	 * @param requestsWithResolvers Array of requests with their resolvers
	 * @param sourceKey Source material key
	 */
	private processBatchedRequests(
		requestsWithResolvers: {
			request: GenerationRequest;
			jobId: string;
			resolve: (result: {
				jobId: string;
				result: Observable<GenerationResult>;
			}) => void;
		}[],
		sourceKey: string,
	): void {
		console.log(
			`🔄 BATCH SERVICE: Processing ${requestsWithResolvers.length} batched requests with shared sync`,
		);

		// Get shared source material from the first request
		const firstRequest = requestsWithResolvers[0].request;
		const sharedSourceMaterial = firstRequest.sourceMaterial;

		if (!sharedSourceMaterial?.selectedArticles?.length) {
			// Fallback to individual processing if no shared material
			requestsWithResolvers.forEach(({ request, jobId, resolve }) => {
				const result = this.processSingleRequest(request, jobId);
				resolve(result);
			});
			return;
		}

		// Upload articles once for all requests
		this.uploadArticlesToVectorStore(
			firstRequest.user,
			sharedSourceMaterial.selectedArticles,
		)
			.pipe(
				switchMap((uploadSuccess) => {
					if (!uploadSuccess) {
						throw new Error(
							'Failed to upload shared articles to vector store',
						);
					}
					// Wait for source material to be ready after upload
					return this.sourceMaterialService.sourceMaterialState$.pipe(
						filter((state) => state === SourceMaterialState.Ready),
						take(1),
					);
				}),
				catchError((error) => {
					console.error('Error in shared file sync:', error);
					// Process individually if shared sync fails
					requestsWithResolvers.forEach(
						({ request, jobId, resolve }) => {
							const result = this.processSingleRequest(
								request,
								jobId,
							);
							resolve(result);
						},
					);
					return throwError(() => error);
				}),
			)
			.subscribe({
				next: () => {
					// Files are synced, now process all requests without individual sync
					requestsWithResolvers.forEach(
						({ request, jobId, resolve }) => {
							const result = this.processSingleRequestWithoutSync(
								request,
								jobId,
							);
							resolve(result);
						},
					);
				},
				error: (error) => {
					console.error(
						'Shared sync error, processing individually:',
						error,
					);
					// Fallback: process each request individually
					requestsWithResolvers.forEach(
						({ request, jobId, resolve }) => {
							const result = this.processSingleRequest(
								request,
								jobId,
							);
							resolve(result);
						},
					);
				},
			});
	}

	/**
	 * Process a single request (original logic)
	 * @param request Generation request
	 * @param existingJobId Optional existing job ID to reuse (for batched requests)
	 * @returns Object with jobId and result observable
	 */
	private processSingleRequest(
		request: GenerationRequest,
		existingJobId?: string,
	): { jobId: string; result: Observable<GenerationResult> } {
		// Use existing job ID or create new one
		const jobId = existingJobId || `sync-${++this.jobIdCounter}`;
		let tempJob: BatchJob;

		if (existingJobId) {
			// Update existing placeholder job to processing status
			tempJob = this.batchJobs.get(existingJobId)!;
			tempJob.status = 'processing';
			console.log(
				`🏗️ BATCH SERVICE: Updated existing job ${existingJobId} to processing status`,
			);
		} else {
			// Ensure steps are defined (should be from backward compatibility check)
			const steps = request.steps || [
				{
					stepId: 'main',
					stepType: 'generate',
					parameters: request.parameters || {},
				},
			];

			// Create new temporary trackable job for cancellation support
			const gameName = this.generateGameName(request);
			tempJob = {
				id: jobId,
				gameType: request.gameType,
				gameName,
				parameters: request.parameters,
				steps: steps,
				currentStepIndex: 0,
				stepResults: new Map<string, any>(),
				completedSteps: [],
				status: 'processing',
				createdAt: new Date(),
				backgroundTaskIds: new Set<string>(),
				selectedArticles: request.sourceMaterial?.selectedArticles,
				cancellationSubject: new Subject<void>(),
			};

			// Add to tracked jobs and emit event
			console.log(
				'🏗️ BATCH SERVICE: STORING new tempJob with ID:',
				jobId,
			);
			this.batchJobs.set(jobId, tempJob);
			this.onJobAdded.emit(tempJob);
		}
		console.log(
			'🏗️ BATCH SERVICE: Total jobs in map:',
			this.batchJobs.size,
		);

		// Create the result observable
		let resultObservable: Observable<GenerationResult>;

		// Check if we need to sync articles first
		const selectedArticles = request.sourceMaterial?.selectedArticles;
		if (selectedArticles && selectedArticles.length > 0) {
			// Set up progress subscription to update notification handle
			// BehaviorSubject emits current state immediately to new subscribers
			let progressSubscription: Subscription | null = null;

			if (request.notificationHandle) {
				progressSubscription = this._materialSyncProgress$
					.pipe(takeUntil(tempJob.cancellationSubject))
					.subscribe(({ message, progress }) => {
						// Only update notification if message is non-empty
						if (message && request.notificationHandle) {
							request.notificationHandle.update({
								message,
								progress,
							});
						}
					});
			}

			// Use ensureMaterialSynced - joins existing sync if running, or starts new one
			resultObservable = this.ensureMaterialSynced(
				request.user,
				selectedArticles,
			).pipe(
				takeUntil(tempJob.cancellationSubject),
				tap(() => {
					// Sync complete - unsubscribe from progress subscription
					if (progressSubscription) {
						progressSubscription.unsubscribe();
						progressSubscription = null;
					}
				}),
				switchMap((syncSuccess) => {
					// Check if job was cancelled during vector store operations
					// Cast needed because status can be changed externally by cancellation mechanism
					if ((tempJob.status as BatchJobStatus) === 'failed') {
						console.log(
							`BATCH-GEN-SERVICE: Job ${jobId} was cancelled during vector store sync. Stopping observable chain.`,
						);
						return throwError(
							() => new Error('Job cancelled by user'),
						);
					}

					if (!syncSuccess) {
						throw new Error(
							'Failed to sync articles to vector store',
						);
					}

					// Check again if job was cancelled before creating background task
					// Cast needed because status can be changed externally by cancellation mechanism
					if ((tempJob.status as BatchJobStatus) === 'failed') {
						console.log(
							`BATCH-GEN-SERVICE: Job ${jobId} was cancelled before background task creation. Stopping observable chain.`,
						);
						return throwError(
							() => new Error('Job cancelled by user'),
						);
					}

					// Sync complete - proceed directly to generation (no need to wait for SourceMaterialState)
					return this.performSynchronousGenerationWithResponseCapture(
						request,
						tempJob,
					);
				}),
				catchError((error) => {
					console.error('Error in synchronous generation:', error);
					// Clean up progress subscription on error
					if (progressSubscription) {
						progressSubscription.unsubscribe();
					}
					// Clean up the temporary job on error
					this.handleTempJobError(
						tempJob,
						`Synchronous generation error: ${error}`,
					);
					return throwError(() => error);
				}),
				finalize(() => {
					// Clean up progress subscription and temporary job when observable completes or errors
					if (progressSubscription) {
						progressSubscription.unsubscribe();
					}
					tempJob.cancellationSubject.complete();
					this.batchJobs.delete(jobId);
				}),
			);
		} else {
			// No articles to upload, proceed with existing logic
			resultObservable =
				this.sourceMaterialService.sourceMaterialState$.pipe(
					takeUntil(tempJob.cancellationSubject),
					filter((state) => state === SourceMaterialState.Ready),
					take(1),
					switchMap(() => {
						// Check if job was cancelled before creating background task
						if (tempJob.status === 'failed') {
							console.log(
								`BATCH-GEN-SERVICE: Job ${jobId} was cancelled before background task creation. Stopping observable chain.`,
							);
							return throwError(
								() => new Error('Job cancelled by user'),
							);
						}
						return this.performSynchronousGenerationWithResponseCapture(
							request,
							tempJob,
						);
					}),
					catchError((error) => {
						console.error(
							'Error in synchronous generation:',
							error,
						);
						// Clean up the temporary job on error
						this.handleTempJobError(
							tempJob,
							`Synchronous generation error: ${error}`,
						);
						return throwError(() => error);
					}),
					finalize(() => {
						// Clean up temporary job when observable completes or errors
						this.batchJobs.delete(jobId);
					}),
				);
		}

		return { jobId, result: resultObservable };
	}

	/**
	 * Process a single request without file sync (files already synced)
	 * @param request Generation request
	 * @param existingJobId Optional existing job ID to reuse (for batched requests)
	 * @returns Object with jobId and result observable
	 */
	private processSingleRequestWithoutSync(
		request: GenerationRequest,
		existingJobId?: string,
	): { jobId: string; result: Observable<GenerationResult> } {
		// Use existing job ID or create new one
		const jobId = existingJobId || `sync-${++this.jobIdCounter}`;
		let tempJob: BatchJob;

		if (existingJobId) {
			// Update existing placeholder job to processing status
			tempJob = this.batchJobs.get(existingJobId)!;
			tempJob.status = 'processing';
			console.log(
				`🏗️ BATCH SERVICE: Updated existing job ${existingJobId} to processing status (no sync)`,
			);
		} else {
			// Ensure steps are defined (should be from backward compatibility check)
			const steps = request.steps || [
				{
					stepId: 'main',
					stepType: 'generate',
					parameters: request.parameters || {},
				},
			];

			// Create new temporary trackable job for cancellation support
			const gameName = this.generateGameName(request);
			tempJob = {
				id: jobId,
				gameType: request.gameType,
				gameName,
				parameters: request.parameters,
				steps: steps,
				currentStepIndex: 0,
				stepResults: new Map<string, any>(),
				completedSteps: [],
				status: 'processing',
				createdAt: new Date(),
				backgroundTaskIds: new Set<string>(),
				selectedArticles: request.sourceMaterial?.selectedArticles,
				cancellationSubject: new Subject<void>(),
			};

			// Add to tracked jobs and emit event
			console.log(
				'🏗️ BATCH SERVICE: STORING new tempJob with ID (no sync):',
				jobId,
			);
			this.batchJobs.set(jobId, tempJob);
			this.onJobAdded.emit(tempJob);
		}

		// Skip file sync and proceed directly to generation
		const resultObservable =
			this.sourceMaterialService.sourceMaterialState$.pipe(
				takeUntil(tempJob.cancellationSubject),
				filter((state) => state === SourceMaterialState.Ready),
				take(1),
				switchMap(() => {
					// Check if job was cancelled before creating background task
					if (tempJob.status === 'failed') {
						console.log(
							`BATCH-GEN-SERVICE: Job ${jobId} was cancelled before background task creation. Stopping observable chain.`,
						);
						return throwError(
							() => new Error('Job cancelled by user'),
						);
					}
					return this.performSynchronousGenerationWithResponseCapture(
						request,
						tempJob,
					);
				}),
				catchError((error) => {
					console.error(
						'Error in synchronous generation (no sync):',
						error,
					);
					// Clean up the temporary job on error
					this.handleTempJobError(
						tempJob,
						`Synchronous generation error: ${error}`,
					);
					return throwError(() => error);
				}),
				finalize(() => {
					// Clean up temporary job when observable completes or errors
					tempJob.cancellationSubject.complete();
					this.batchJobs.delete(jobId);
				}),
			);

		return { jobId, result: resultObservable };
	}

	/**
	 * Perform the actual synchronous generation with response ID capture
	 * @param request The generation request
	 * @param tempJob The temporary job for tracking
	 * @returns Observable with generation result
	 */
	private performSynchronousGenerationWithResponseCapture(
		request: GenerationRequest,
		tempJob: BatchJob,
	): Observable<GenerationResult> {
		try {
			// Create story for AI conversation
			const story: Story | undefined = this.storyService.CreateStory();
			if (!story) {
				return throwError(
					() => new Error('Failed to create story for generation'),
				);
			}

			// Generate prompts using utilities
			const prompts = this.generationUtils.createPrompts(
				request,
				this.settingsService,
			);

			// Add messages to story
			story.messages.push({
				role: 'system',
				content: prompts.system,
			} as AIMessage);

			story.messages.push({
				role: 'user',
				content: prompts.user,
			} as AIMessage);

			// Prepare prompt options - always use isBackground: true for reliable HTTP polling
			const promptOptions = request.sourceMaterial?.fileHandles.length
				? {
						vectorStoreHandles: [request.user.vectorStoreId],
						fileHandlesToInclude:
							request.sourceMaterial.fileHandles,
						isBackgroundTask: true,
						lifecycleHooks: {
							onTaskCreated: (taskId: string) => {
								// Only track the task if job is still active (prevent orphaned tasks)
								if (
									tempJob.status === 'processing' ||
									tempJob.status === 'queued'
								) {
									tempJob.backgroundTaskIds.add(taskId);
									console.log(
										'[TASK-TRACK] Added backgroundTaskId:',
										taskId,
										`(total: ${tempJob.backgroundTaskIds.size}, job status: ${tempJob.status})`,
									);
								} else {
									console.log(
										'[TASK-TRACK] Skipping orphaned task:',
										taskId,
										`(job status: ${tempJob.status})`,
									);
									// Cancel the orphaned task to stop polling
									this.backgroundTaskQueue.cancelTask(taskId);
									console.log(
										'[TASK-TRACK] Cancelled orphaned task:',
										taskId,
									);
								}
								request.lifecycleHooks?.onTaskCreated?.(taskId);
							},
							onTaskStart: (taskId: string) => {
								request.lifecycleHooks?.onTaskStart?.(taskId);
							},
							onRequestComplete: (
								taskId: string,
								response: any,
							) => {
								request.lifecycleHooks?.onRequestComplete?.(
									taskId,
									response,
								);
							},
							onPollStart: (
								taskId: string,
								responseId: string,
							) => {
								request.lifecycleHooks?.onPollStart?.(
									taskId,
									responseId,
								);
							},
							onPollAttempt: (
								taskId: string,
								attempt: number,
								maxAttempts: number,
							) => {
								request.lifecycleHooks?.onPollAttempt?.(
									taskId,
									attempt,
									maxAttempts,
								);
							},
							onPollComplete: (taskId: string, response: any) => {
								request.lifecycleHooks?.onPollComplete?.(
									taskId,
									response,
								);
							},
							onValidationFailed: (
								taskId: string,
								response: any,
								retryCount: number,
							) => {
								request.lifecycleHooks?.onValidationFailed?.(
									taskId,
									response,
									retryCount,
								);
							},
							onRetry: (
								taskId: string,
								retryCount: number,
								reason: 'error' | 'validation',
							) => {
								request.lifecycleHooks?.onRetry?.(
									taskId,
									retryCount,
									reason,
								);
							},
							onTaskComplete: (
								taskId: string,
								response: any,
								duration: number,
							) => {
								request.lifecycleHooks?.onTaskComplete?.(
									taskId,
									response,
									duration,
								);
							},
							onTaskError: (taskId: string, error: unknown) => {
								request.lifecycleHooks?.onTaskError?.(
									taskId,
									error,
								);
							},
							modelConfig:
								request.steps?.[0]?.modelConfig ||
								request.modelConfig,
						},
						cancelCallback: request.cancelCallback,
					}
				: {
						vectorStoreHandles: [request.user.vectorStoreId],
						isBackgroundTask: true,
						lifecycleHooks: {
							onTaskCreated: (taskId: string) => {
								// Only track the task if job is still active (prevent orphaned tasks)
								if (
									tempJob.status === 'processing' ||
									tempJob.status === 'queued'
								) {
									tempJob.backgroundTaskIds.add(taskId);
									console.log(
										'[TASK-TRACK] Added backgroundTaskId:',
										taskId,
										`(total: ${tempJob.backgroundTaskIds.size}, job status: ${tempJob.status})`,
									);
								} else {
									console.log(
										'[TASK-TRACK] Skipping orphaned task:',
										taskId,
										`(job status: ${tempJob.status})`,
									);
									// Cancel the orphaned task to stop polling
									this.backgroundTaskQueue.cancelTask(taskId);
									console.log(
										'[TASK-TRACK] Cancelled orphaned task:',
										taskId,
									);
								}
								request.lifecycleHooks?.onTaskCreated?.(taskId);
							},
							onTaskStart: (taskId: string) => {
								request.lifecycleHooks?.onTaskStart?.(taskId);
							},
							onRequestComplete: (
								taskId: string,
								response: any,
							) => {
								request.lifecycleHooks?.onRequestComplete?.(
									taskId,
									response,
								);
							},
							onPollStart: (
								taskId: string,
								responseId: string,
							) => {
								request.lifecycleHooks?.onPollStart?.(
									taskId,
									responseId,
								);
							},
							onPollAttempt: (
								taskId: string,
								attempt: number,
								maxAttempts: number,
							) => {
								request.lifecycleHooks?.onPollAttempt?.(
									taskId,
									attempt,
									maxAttempts,
								);
							},
							onPollComplete: (taskId: string, response: any) => {
								request.lifecycleHooks?.onPollComplete?.(
									taskId,
									response,
								);
							},
							onValidationFailed: (
								taskId: string,
								response: any,
								retryCount: number,
							) => {
								request.lifecycleHooks?.onValidationFailed?.(
									taskId,
									response,
									retryCount,
								);
							},
							onRetry: (
								taskId: string,
								retryCount: number,
								reason: 'error' | 'validation',
							) => {
								request.lifecycleHooks?.onRetry?.(
									taskId,
									retryCount,
									reason,
								);
							},
							onTaskComplete: (
								taskId: string,
								response: any,
								duration: number,
							) => {
								request.lifecycleHooks?.onTaskComplete?.(
									taskId,
									response,
									duration,
								);
							},
							onTaskError: (taskId: string, error: unknown) => {
								request.lifecycleHooks?.onTaskError?.(
									taskId,
									error,
								);
							},
						},
						modelConfig:
							request.steps?.[0]?.modelConfig ||
							request.modelConfig,
						cancelCallback: request.cancelCallback,
					};

			// For now, use the regular AI service call and let's try a different approach
			// Make AI request with background polling for reliability
			return this.aiService
				.CreateResponse(story.messages, promptOptions)
				.pipe(
					tap((response: AIAPIResponse) => {
						// Save assistant response to story
						story.messages.push({
							role: 'assistant',
							content: response.rawResponse as string,
						} as AIMessage);

						console.log(
							'=== BATCH-GEN-SERVICE: Processing AIAPIResponse ===',
						);
						console.log(
							'  - backgroundTaskIds already set via callback:',
							Array.from(tempJob.backgroundTaskIds),
						);
						console.log(
							'  - rawResponse length:',
							response.rawResponse?.length,
						);
					}),
					switchMap((response: AIAPIResponse) => {
						// Process response using utilities
						const result = this.generationUtils.processResponse(
							request.gameType,
							response,
							request.parameters,
						);

						if (result.success) {
							// Mark temporary job as completed
							this.handleTempJobSuccess(tempJob);
							return of(result);
						} else {
							return throwError(
								() =>
									new Error(
										result.error ||
											'Unknown processing error',
									),
							);
						}
					}),
					catchError((error) => {
						console.error(
							'AI service error in synchronous generation:',
							error,
						);
						return throwError(
							() => new Error(`Generation failed: ${error}`),
						);
					}),
				);
		} catch (error) {
			return throwError(
				() => new Error(`Generation setup error: ${error}`),
			);
		}
	}

	/**
	 * Start the actual generation process for a batch job
	 * @param jobId The job ID
	 * @param request The generation request
	 */
	private async startBatchGeneration(
		jobId: string,
		request: GenerationRequest,
	): Promise<void> {
		const job = this.batchJobs.get(jobId);
		if (!job) return;

		// Wait for SourceMaterialState to be Ready before proceeding
		this.sourceMaterialService.sourceMaterialState$
			.pipe(
				filter((state) => state === SourceMaterialState.Ready),
				take(1),
				switchMap(() => {
					this.performGeneration(jobId, request);
					return of(null);
				}),
			)
			.subscribe();
	}

	/**
	 * Perform the actual generation after source material is ready
	 * Now supports multi-step generation pipeline
	 * @param jobId The job ID
	 * @param request The generation request
	 */
	private async performGeneration(
		jobId: string,
		request: GenerationRequest,
	): Promise<void> {
		const job = this.batchJobs.get(jobId);
		if (!job) return;

		try {
			job.status = 'processing';

			// Check if we need to upload articles first
			if (job.selectedArticles && job.selectedArticles.length > 0) {
				const uploadSuccess = await firstValueFrom(
					this.uploadArticlesToVectorStore(
						request.user,
						job.selectedArticles,
					),
				);

				if (!uploadSuccess) {
					throw new Error(
						'Failed to upload articles to vector store for batch job',
					);
				}
			}

			// Execute all steps sequentially
			await this.executeAllSteps(jobId, request);
		} catch (error) {
			this.handleBatchJobError(jobId, `Generation setup error: ${error}`);
		}
	}

	/**
	 * Execute all steps in the generation pipeline sequentially
	 * @param jobId The job ID
	 * @param request The generation request
	 */
	private async executeAllSteps(
		jobId: string,
		request: GenerationRequest,
	): Promise<void> {
		const job = this.batchJobs.get(jobId);
		if (!job || !job.steps) return;

		// Process each step sequentially
		for (let i = 0; i < job.steps.length; i++) {
			job.currentStepIndex = i;
			const step = job.steps[i];

			// Check if job was cancelled before starting this step
			if (job.status === 'failed') {
				console.log(
					'🛑 BATCH-GEN-SERVICE: Job was cancelled, stopping step execution',
				);
				return;
			}

			console.log(
				`🔄 BATCH-GEN-SERVICE: Executing step ${i + 1}/${job.steps.length}: ${step.stepId} (${step.stepType})`,
			);

			try {
				// Execute this step
				const stepResult = await this.processNextStep(
					jobId,
					request,
					step,
				);

				// Store the result
				job.stepResults.set(step.stepId, stepResult);
				job.completedSteps.push(step.stepId);

				console.log(
					`✅ BATCH-GEN-SERVICE: Step ${step.stepId} completed successfully`,
				);
			} catch (error) {
				console.error(
					`❌ BATCH-GEN-SERVICE: Step ${step.stepId} failed:`,
					error,
				);
				this.handleBatchJobError(
					jobId,
					`Step ${step.stepId} (${step.stepType}) failed: ${error}`,
				);
				return; // Stop processing further steps
			}
		}

		// All steps completed successfully - finalize the job
		await this.finalizeMultiStepJob(jobId, request);
	}

	/**
	 * Process a single step in the generation pipeline
	 * @param jobId The job ID
	 * @param request The original generation request
	 * @param step The step to process
	 * @returns The result of this step
	 */
	private async processNextStep(
		jobId: string,
		request: GenerationRequest,
		step: GenerationStep,
	): Promise<any> {
		const job = this.batchJobs.get(jobId);
		if (!job) throw new Error('Job not found');

		// Check if job was cancelled before processing this step
		if (job.status === 'failed') {
			throw new Error('Job was cancelled by user');
		}

		// Get results from dependent steps
		const previousResults = this.getResultsForDependencies(
			step.dependsOn,
			job.stepResults,
		);

		// Enrich step parameters with previous results
		// IMPORTANT: Merge original request.parameters with step.parameters
		// This preserves critical parameters like gameService that are needed for optimization
		const enrichedRequest: GenerationRequest = {
			...request,
			parameters: {
				...request.parameters, // Start with original parameters (includes gameService, etc.)
				...step.parameters, // Override/add step-specific parameters
				stepType: step.stepType, // Preserve stepType from step definition
				previousStepResults: previousResults,
			},
		};

		// DEBUG: Check if gameService is present
		console.log('[processNextStep] 🔍 DEBUG enrichedRequest.parameters:', {
			hasGameService: !!enrichedRequest.parameters['gameService'],
			gameServiceType: typeof enrichedRequest.parameters['gameService'],
			hasResetMethod:
				enrichedRequest.parameters['gameService'] &&
				typeof enrichedRequest.parameters['gameService'][
					'resetForOptimization'
				] === 'function',
			generationType: enrichedRequest.parameters['generationType'],
		});

		// Create story for AI conversation
		const story: Story | undefined = this.storyService.CreateStory();
		if (!story) {
			throw new Error('Failed to create story for generation');
		}

		// Generate prompts using utilities (with enriched parameters)
		const prompts = this.generationUtils.createPrompts(
			enrichedRequest,
			this.settingsService,
		);

		// DEBUG: Log the prompts object structure
		console.log('🔍 BATCH-GEN-SERVICE: Prompts object:', {
			hasMultipleRequests: 'multipleRequests' in prompts,
			multipleRequestsValue: prompts.multipleRequests,
			multipleRequestsLength: prompts.multipleRequests?.length,
			systemLength: prompts.system?.length,
			userLength: prompts.user?.length,
		});

		// Prepare prompt options for background task
		const promptOptions = request.sourceMaterial?.fileHandles.length
			? {
					vectorStoreHandles: [request.user.vectorStoreId],
					fileHandlesToInclude: request.sourceMaterial.fileHandles,
					isBackgroundTask: true,
					modelConfig: step.modelConfig || request.modelConfig,
					lifecycleHooks: {
						onTaskCreated: (taskId: string) => {
							if (job) {
								// Only track the task if job is still active (prevent orphaned tasks)
								if (
									job.status === 'processing' ||
									job.status === 'queued'
								) {
									job.backgroundTaskIds.add(taskId);
									console.log(
										`[TASK-TRACK] Step ${step.stepId} - Added backgroundTaskId:`,
										taskId,
										`(total: ${job.backgroundTaskIds.size}, job status: ${job.status})`,
									);
								} else {
									console.log(
										`[TASK-TRACK] Step ${step.stepId} - Skipping orphaned task:`,
										taskId,
										`(job status: ${job.status})`,
									);
									// Cancel the orphaned task to stop polling
									this.backgroundTaskQueue.cancelTask(taskId);
									console.log(
										`[TASK-TRACK] Step ${step.stepId} - Cancelled orphaned task:`,
										taskId,
									);
								}
							}
							request.lifecycleHooks?.onTaskCreated?.(taskId);
						},
						onTaskStart: (taskId: string) => {
							request.lifecycleHooks?.onTaskStart?.(taskId);
						},
						onRequestComplete: (taskId: string, response: any) => {
							request.lifecycleHooks?.onRequestComplete?.(
								taskId,
								response,
							);
						},
						onPollStart: (taskId: string, responseId: string) => {
							request.lifecycleHooks?.onPollStart?.(
								taskId,
								responseId,
							);
						},
						onPollAttempt: (
							taskId: string,
							attempt: number,
							maxAttempts: number,
						) => {
							request.lifecycleHooks?.onPollAttempt?.(
								taskId,
								attempt,
								maxAttempts,
							);
						},
						onPollComplete: (taskId: string, response: any) => {
							request.lifecycleHooks?.onPollComplete?.(
								taskId,
								response,
							);
						},
						onValidationFailed: (
							taskId: string,
							response: any,
							retryCount: number,
						) => {
							request.lifecycleHooks?.onValidationFailed?.(
								taskId,
								response,
								retryCount,
							);
						},
						onRetry: (
							taskId: string,
							retryCount: number,
							reason: 'error' | 'validation',
						) => {
							request.lifecycleHooks?.onRetry?.(
								taskId,
								retryCount,
								reason,
							);
						},
						onTaskComplete: (
							taskId: string,
							response: any,
							duration: number,
						) => {
							request.lifecycleHooks?.onTaskComplete?.(
								taskId,
								response,
								duration,
							);
						},
						onTaskError: (taskId: string, error: unknown) => {
							request.lifecycleHooks?.onTaskError?.(
								taskId,
								error,
							);
						},
						cancelCallback: request.cancelCallback,
					},
				}
			: {
					vectorStoreHandles: [request.user.vectorStoreId],
					isBackgroundTask: true,
					modelConfig: step.modelConfig || request.modelConfig,
					lifecycleHooks: {
						onTaskCreated: (taskId: string) => {
							if (job) {
								// Only track the task if job is still active (prevent orphaned tasks)
								if (
									job.status === 'processing' ||
									job.status === 'queued'
								) {
									job.backgroundTaskIds.add(taskId);
									console.log(
										`[TASK-TRACK] Step ${step.stepId} - Added backgroundTaskId:`,
										taskId,
										`(total: ${job.backgroundTaskIds.size}, job status: ${job.status})`,
									);
								} else {
									console.log(
										`[TASK-TRACK] Step ${step.stepId} - Skipping orphaned task:`,
										taskId,
										`(job status: ${job.status})`,
									);
									// Cancel the orphaned task to stop polling
									this.backgroundTaskQueue.cancelTask(taskId);
									console.log(
										`[TASK-TRACK] Step ${step.stepId} - Cancelled orphaned task:`,
										taskId,
									);
								}
							}
							request.lifecycleHooks?.onTaskCreated?.(taskId);
						},
						onTaskStart: (taskId: string) => {
							request.lifecycleHooks?.onTaskStart?.(taskId);
						},
						onRequestComplete: (taskId: string, response: any) => {
							request.lifecycleHooks?.onRequestComplete?.(
								taskId,
								response,
							);
						},
						onPollStart: (taskId: string, responseId: string) => {
							request.lifecycleHooks?.onPollStart?.(
								taskId,
								responseId,
							);
						},
						onPollAttempt: (
							taskId: string,
							attempt: number,
							maxAttempts: number,
						) => {
							request.lifecycleHooks?.onPollAttempt?.(
								taskId,
								attempt,
								maxAttempts,
							);
						},
						onPollComplete: (taskId: string, response: any) => {
							request.lifecycleHooks?.onPollComplete?.(
								taskId,
								response,
							);
						},
						onValidationFailed: (
							taskId: string,
							response: any,
							retryCount: number,
						) => {
							request.lifecycleHooks?.onValidationFailed?.(
								taskId,
								response,
								retryCount,
							);
						},
						onRetry: (
							taskId: string,
							retryCount: number,
							reason: 'error' | 'validation',
						) => {
							request.lifecycleHooks?.onRetry?.(
								taskId,
								retryCount,
								reason,
							);
						},
						onTaskComplete: (
							taskId: string,
							response: any,
							duration: number,
						) => {
							request.lifecycleHooks?.onTaskComplete?.(
								taskId,
								response,
								duration,
							);
						},
						onTaskError: (taskId: string, error: unknown) => {
							request.lifecycleHooks?.onTaskError?.(
								taskId,
								error,
							);
						},
						cancelCallback: request.cancelCallback,
					},
				};

		// CHECK FOR MULTIPLE REQUESTS (multi-request generation)
		if (prompts.multipleRequests && prompts.multipleRequests.length > 0) {
			console.log(
				`🔄 BATCH-GEN-SERVICE: Multi-request generation detected for step ${step.stepId}`,
			);
			console.log(
				`   Creating ${prompts.multipleRequests.length} parallel requests`,
			);

			// Track progress for this multi-request step
			let completedRequests = 0;
			const totalRequests = prompts.multipleRequests.length;

			// Create array of observables (one per topic)
			const requestObservables = prompts.multipleRequests.map(
				(promptSet, idx) => {
					console.log(
						`   [${idx}] Creating request for topic: "${promptSet.topicTitle}" (index: ${promptSet.topicIndex})`,
					);

					// Create individual story for this request
					const individualStory: Story | undefined =
						this.storyService.CreateStory();
					if (!individualStory) {
						return throwError(
							() =>
								new Error(
									'Failed to create story for multi-request generation',
								),
						);
					}

					// Add system and user messages for this specific topic
					individualStory.messages.push({
						role: 'system',
						content: promptSet.system,
					} as AIMessage);

					individualStory.messages.push({
						role: 'user',
						content: promptSet.user,
					} as AIMessage);

					// Make AI request for this topic
					return this.aiService
						.CreateResponse(individualStory.messages, promptOptions)
						.pipe(
							tap((response: AIAPIResponse) => {
								// Save assistant response to story
								individualStory.messages.push({
									role: 'assistant',
									content: response.rawResponse as string,
								} as AIMessage);
								console.log(
									`   [${idx}] Response received for topic: "${promptSet.topicTitle}"`,
								);
							}),
							switchMap((response: AIAPIResponse) => {
								// Process response for this individual topic
								// Use generateTextContent flag so processResponse knows how to handle it
								const processingParams = {
									...enrichedRequest.parameters,
									generateTextContent: true, // Individual topic uses text content processing
									forRenderer: true, // Batch generation creates export data for renderer
									topicTitle: promptSet.topicTitle,
								};

								const result =
									this.generationUtils.processResponse(
										request.gameType,
										response,
										processingParams,
									);

								if (result.success) {
									// Attach topic index for aggregation
									return of({
										...result,
										topicIndex: promptSet.topicIndex,
										topicTitle: promptSet.topicTitle,
									});
								} else {
									console.error(
										`   [${idx}] Failed to process response for topic "${promptSet.topicTitle}":`,
										result.error,
									);
									return throwError(
										() =>
											new Error(
												result.error ||
													'Unknown processing error',
											),
									);
								}
							}),
							tap(() => {
								// Emit progress event after each request completes (success or error)
								completedRequests++;
								this.onStepProgress.emit({
									jobId: jobId,
									stepId: step.stepId,
									completed: completedRequests,
									total: totalRequests,
									currentItem: promptSet.topicTitle,
								});
								console.log(
									`   📊 Progress: ${completedRequests}/${totalRequests} requests completed`,
								);
							}),
							catchError((error) => {
								console.error(
									`   [${idx}] Error in request for topic "${promptSet.topicTitle}":`,
									error,
								);
								// Emit progress even on error
								completedRequests++;
								this.onStepProgress.emit({
									jobId: jobId,
									stepId: step.stepId,
									completed: completedRequests,
									total: totalRequests,
									currentItem: promptSet.topicTitle,
								});
								// Return error result but don't fail entire batch
								return of({
									success: false,
									error: `Failed to generate content for topic "${promptSet.topicTitle}": ${error}`,
									topicIndex: promptSet.topicIndex,
									topicTitle: promptSet.topicTitle,
								});
							}),
						);
				},
			);

			// Run all requests in PARALLEL using forkJoin (like component does)
			console.log(
				`🚀 BATCH-GEN-SERVICE: Starting parallel execution with forkJoin`,
			);
			return firstValueFrom(
				forkJoin(requestObservables).pipe(
					map((results) => {
						console.log(
							`✅ BATCH-GEN-SERVICE: All ${results.length} requests completed`,
						);
						console.log(
							`   Successful: ${results.filter((r) => r.success).length}`,
						);
						console.log(
							`   Failed: ${results.filter((r) => !r.success).length}`,
						);

						// Aggregate results using processResponse with aggregation flag
						const aggregationParams = {
							...enrichedRequest.parameters,
							isMultiRequestAggregation: true,
							multiRequestResults: results,
						};

						// Call processResponse to aggregate results
						const aggregatedResult =
							this.generationUtils.processResponse(
								request.gameType,
								{ json: results } as any, // Wrap results in response format
								aggregationParams,
							);

						console.log(
							`📦 BATCH-GEN-SERVICE: Aggregation complete. Success: ${aggregatedResult.success}`,
						);
						return aggregatedResult;
					}),
				),
			);
		}

		// SINGLE REQUEST PATH (existing logic - backward compatible)
		// Create story for AI conversation
		if (!story) {
			throw new Error('Failed to create story for generation');
		}

		// Add messages to story
		story.messages.push({
			role: 'system',
			content: prompts.system,
		} as AIMessage);

		story.messages.push({
			role: 'user',
			content: prompts.user,
		} as AIMessage);

		// Make AI request and return the result as a promise
		return firstValueFrom(
			this.aiService.CreateResponse(story.messages, promptOptions).pipe(
				tap((response: AIAPIResponse) => {
					// Save assistant response to story
					story.messages.push({
						role: 'assistant',
						content: response.rawResponse as string,
					} as AIMessage);
				}),
				switchMap((response: AIAPIResponse) => {
					// Process response using utilities
					const result = this.generationUtils.processResponse(
						request.gameType,
						response,
						enrichedRequest.parameters,
					);

					if (result.success) {
						return of(result);
					} else {
						return throwError(
							() =>
								new Error(
									result.error || 'Unknown processing error',
								),
						);
					}
				}),
			),
		);
	}

	/**
	 * Get results from dependent steps
	 * @param dependsOn Array of stepIds this step depends on
	 * @param stepResults Map of all step results
	 * @returns Object with results from dependent steps
	 */
	private getResultsForDependencies(
		dependsOn: string[] | undefined,
		stepResults: Map<string, any>,
	): any {
		if (!dependsOn || dependsOn.length === 0) {
			return {};
		}

		const results: any = {};
		for (const stepId of dependsOn) {
			if (stepResults.has(stepId)) {
				results[stepId] = stepResults.get(stepId);
			}
		}
		return results;
	}

	/**
	 * Finalize a multi-step job after all steps are complete
	 * @param jobId The job ID
	 * @param request The original generation request
	 */
	private async finalizeMultiStepJob(
		jobId: string,
		request: GenerationRequest,
	): Promise<void> {
		const job = this.batchJobs.get(jobId);
		if (!job) return;

		// Get the final result (from the last step)
		const lastStep = job.steps[job.steps.length - 1];
		const finalResult = job.stepResults.get(lastStep.stepId);

		if (finalResult && finalResult.success && finalResult.gameData) {
			this.handleBatchJobSuccess(jobId, finalResult, request);
		} else {
			this.handleBatchJobError(
				jobId,
				'Final step did not produce valid game data',
			);
		}
	}

	/**
	 * Handle successful batch job completion
	 * @param jobId Job ID
	 * @param result Generation result
	 * @param request Original request
	 */
	private handleBatchJobSuccess(
		jobId: string,
		result: GenerationResult,
		request: GenerationRequest,
	): void {
		const job = this.batchJobs.get(jobId);
		if (!job || !result.gameData) return;

		// Log received gameData structure for debugging
		console.log(
			`📥 BATCH-GEN-SERVICE: Received gameData for job "${job.gameName}":`,
			{
				gameType: result.gameData.type,
				hasTopics: 'topics' in result.gameData,
				topicsCount: (result.gameData as any).topics?.length || 0,
				topicsWithParagraphs:
					(result.gameData as any).topics?.filter(
						(t: any) => t.paragraphs && t.paragraphs.length > 0,
					).length || 0,
				topics:
					(result.gameData as any).topics?.map((t: any) => ({
						title: t.title,
						paragraphCount: t.paragraphs?.length || 0,
						hasParagraphs:
							!!t.paragraphs && t.paragraphs.length > 0,
					})) || [],
				// Crossword-specific debugging
				isCrossword:
					result.gameData.type === PluginType.CrosswordPuzzle,
				gridRows: (result.gameData as any).gridRows,
				gridCols: (result.gameData as any).gridCols,
				hasGrid: !!(result.gameData as any).grid,
				hasPlacedWords: !!(result.gameData as any).placedWords,
			},
		);

		// Create Export object for the generated game
		const exportData: Export = {
			id: -1, // Unsaved
			name: job.gameName,
			date: Math.floor(Date.now() / 1000), // Unix timestamp in seconds
			creator: request.user.userName,
			version: this.settingsService.AppDataVersion,
			gameData: result.gameData,
		};

		// Update job
		job.status = 'completed';
		job.result = exportData;
		job.completedAt = new Date();
		job.cancellationSubject.complete();

		// Emit completion event
		this.onJobCompleted.emit(job);

		// Show notification
		// this.notificationService.ShowToast(
		// 	`"${job.gameName}" on luotu ja lisätty Tallennetut-sivulle`,
		// 	'success',
		// );
	}

	/**
	 * Handle batch job error
	 * @param jobId Job ID
	 * @param error Error message
	 */
	private handleBatchJobError(jobId: string, error: string): void {
		const job = this.batchJobs.get(jobId);
		if (!job) return;

		job.status = 'failed';
		job.error = error;
		job.completedAt = new Date();
		job.cancellationSubject.complete();

		// Emit error event
		this.onJobFailed.emit(job);

		// Show error notification
		// this.notificationService.ShowToast(
		// 	`Pelin "${job.gameName}" luominen epäonnistui taustalla`,
		// 	'warning',
		// );
	}

	/**
	 * Get all batch jobs
	 * @returns Array of batch jobs
	 */
	getBatchJobs(): BatchJob[] {
		return Array.from(this.batchJobs.values());
	}

	/**
	 * Get completed batch jobs (with results)
	 * @returns Array of completed jobs
	 */
	getCompletedBatchJobs(): BatchJob[] {
		return this.getBatchJobs().filter(
			(job) => job.status === 'completed' && job.result,
		);
	}

	/**
	 * Get a specific batch job by ID
	 * @param jobId Job ID
	 * @returns Batch job or undefined
	 */
	getBatchJob(jobId: string): BatchJob | undefined {
		return this.batchJobs.get(jobId);
	}

	/**
	 * Cancel a batch job (for queued or processing jobs) using BackgroundTaskQueue
	 * @param jobId Job ID
	 * @returns Observable that completes when cancellation is done
	 */
	cancelBatchJob(jobId: string): Observable<boolean> {
		const job = this.batchJobs.get(jobId);

		// Collect diagnostic data
		const diagnostics: {
			jobId: string;
			jobFound: boolean;
			timestamp: string;
			jobStatus?: string;
			gameName?: string;
			backgroundTaskIds?: string[];
			backgroundTaskCount?: number;
			canCancel?: boolean;
			reason?: string;
			cancellationSubjectTriggered?: boolean;
			queueStatus?: unknown;
			activeTasksInQueue?: string[];
			queuedTasksInQueue?: string[];
			cancellationResults?: Array<{ taskId: string; success: boolean }>;
			anySuccess?: boolean;
			result?: string;
		} = {
			jobId: jobId,
			jobFound: !!job,
			timestamp: new Date().toISOString(),
		};

		if (!job) {
			console.error(
				'[CANCEL-DIAG] cancelBatchJob() - Job not found',
				diagnostics,
			);
			return throwError(() => new Error('Job not found'));
		}

		diagnostics.jobStatus = job.status;
		diagnostics.gameName = job.gameName;
		diagnostics.backgroundTaskIds = Array.from(job.backgroundTaskIds);
		diagnostics.backgroundTaskCount = job.backgroundTaskIds.size;

		// Only allow canceling queued or processing jobs
		if (job.status !== 'queued' && job.status !== 'processing') {
			diagnostics.canCancel = false;
			diagnostics.reason = `Status is ${job.status}`;
			console.log(
				'[CANCEL-DIAG] cancelBatchJob() - Cannot cancel',
				diagnostics,
			);
			return of(false);
		}

		diagnostics.canCancel = true;

		// Trigger cancellation via Subject to stop any running observable chains
		job.cancellationSubject.next();
		job.cancellationSubject.complete();
		diagnostics.cancellationSubjectTriggered = true;

		// Get BackgroundTaskQueue state
		const queueStatus = this.backgroundTaskQueue.getQueueStatus();
		diagnostics.queueStatus = queueStatus;

		// Get active and queued tasks for diagnostic
		const activeTasks = (
			this.backgroundTaskQueue as unknown as {
				activeTasks: Map<string, unknown>;
			}
		)['activeTasks'];
		const queuedTasks = (
			this.backgroundTaskQueue as unknown as {
				queue: Array<{ id: string }>;
			}
		)['queue'];
		diagnostics.activeTasksInQueue = activeTasks
			? Array.from(activeTasks.keys())
			: [];
		diagnostics.queuedTasksInQueue = queuedTasks
			? queuedTasks.map((t) => t.id)
			: [];

		// If job has background task IDs, cancel all via BackgroundTaskQueue
		if (job.backgroundTaskIds && job.backgroundTaskIds.size > 0) {
			// Cancel all tracked background tasks and collect results
			diagnostics.cancellationResults = [];
			let anySuccess = false;

			for (const taskId of job.backgroundTaskIds) {
				const success = this.backgroundTaskQueue.cancelTask(taskId);
				diagnostics.cancellationResults.push({ taskId, success });
				if (success) anySuccess = true;
			}

			// Clear the set after cancellation attempt
			job.backgroundTaskIds.clear();
			diagnostics.anySuccess = anySuccess;

			if (anySuccess) {
				// Update job status after successful cancellation
				job.status = 'failed';
				job.error = 'Peruutettu käyttäjän toimesta';
				job.completedAt = new Date();

				// Emit cancellation event
				this.onJobFailed.emit(job);

				// Show notification
				// this.notificationService.ShowToast(
				// 	`"${job.gameName}" luominen peruutettu`,
				// 	'warning',
				// );

				diagnostics.result = 'success';
				console.log(
					'[CANCEL-DIAG] cancelBatchJob() completed',
					diagnostics,
				);
				return of(true);
			} else {
				// Still mark the job as cancelled locally even if background task cancellation fails
				job.status = 'failed';
				job.error =
					'Peruutettu käyttäjän toimesta (taustatyötä ei voitu perua)';
				job.completedAt = new Date();

				// Emit cancellation event
				this.onJobFailed.emit(job);

				// Show notification
				this.notificationService.ShowToast(
					`"${job.gameName}" luominen peruutettu (taustatyötä ei voitu perua)`,
					'warning',
				);

				diagnostics.result = 'partial-success';
				console.warn(
					'[CANCEL-DIAG] cancelBatchJob() partial success',
					diagnostics,
				);
				return of(true);
			}
		} else {
			// No background task IDs to cancel, just update job status
			job.status = 'failed';
			job.error = 'Peruutettu käyttäjän toimesta';
			job.completedAt = new Date();

			// Emit cancellation event
			this.onJobFailed.emit(job);

			// Show notification
			// this.notificationService.ShowToast(
			// 	`"${job.gameName}" luominen peruutettu`,
			// 	'warning',
			// );

			diagnostics.result = 'no-background-tasks';
			console.log(
				'[CANCEL-DIAG] cancelBatchJob() completed',
				diagnostics,
			);
			return of(true);
		}
	}

	/**
	 * Cancel and delete a batch job with confirmation
	 * @param jobId Job ID
	 * @returns Observable that completes when cancellation and deletion is done
	 */
	cancelAndDeleteBatchJob(jobId: string): Observable<boolean> {
		return this.cancelBatchJob(jobId).pipe(
			tap(() => {
				// Delete the job after cancellation
				this.batchJobs.delete(jobId);
			}),
		);
	}

	/**
	 * Delete a batch job
	 * @param jobId Job ID
	 */
	deleteBatchJob(jobId: string): void {
		this.batchJobs.delete(jobId);
	}

	/**
	 * Clear all completed and failed jobs
	 */
	clearCompletedJobs(): void {
		const toDelete = Array.from(this.batchJobs.entries())
			.filter(
				([, job]) =>
					job.status === 'completed' || job.status === 'failed',
			)
			.map(([id]) => id);

		toDelete.forEach((id) => this.batchJobs.delete(id));
	}

	/**
	 * Get count of jobs by status
	 * @param status Job status to count
	 * @returns Number of jobs with the given status
	 */
	getJobCount(status: BatchJobStatus): number {
		return this.getBatchJobs().filter((job) => job.status === status)
			.length;
	}

	/**
	 * Get only cancellable batch jobs (those with a valid backgroundTaskId)
	 * @returns Array of cancellable jobs
	 */
	getCancellableJobs(): BatchJob[] {
		const allJobs = this.getBatchJobs();
		const cancellableJobs = allJobs.filter((job) =>
			this.isJobCancellable(job),
		);
		console.log('🔍 BATCH SERVICE: getCancellableJobs called');
		console.log(
			'🔍 BATCH SERVICE: All jobs:',
			allJobs.map((j) => ({ id: j.id, status: j.status })),
		);
		console.log(
			'🔍 BATCH SERVICE: Cancellable jobs:',
			cancellableJobs.map((j) => ({ id: j.id, status: j.status })),
		);
		return cancellableJobs;
	}

	/**
	 * Check if a specific job is cancellable
	 * @param job Batch job to check
	 * @returns True if job can be cancelled
	 */
	isJobCancellable(job: BatchJob): boolean {
		// Job must be in cancellable state and have a backgroundTaskId
		return (
			(job.status === 'queued' || job.status === 'processing') &&
			job.backgroundTaskIds.size > 0
		);
	}

	/**
	 * Check if a specific job is cancellable by ID
	 * @param jobId Job ID to check
	 * @returns True if job can be cancelled
	 */
	isJobCancellableById(jobId: string): boolean {
		const job = this.batchJobs.get(jobId);
		return job ? this.isJobCancellable(job) : false;
	}

	/**
	 * Generate a descriptive name for the game
	 * @param request Generation request
	 * @returns Generated game name
	 */
	private generateGameName(request: GenerationRequest): string {
		const gameTypeName = 'Peli';

		if (request.parameters.questionTopic?.trim()) {
			return `${gameTypeName}: ${request.parameters.questionTopic}`;
		} else if (request.sourceMaterial?.selectedArticles.length) {
			const firstArticle = request.sourceMaterial.selectedArticles[0];
			const articleName = firstArticle.title || 'Lähdemateriaali';
			return `${gameTypeName}: ${articleName}`;
		} else {
			const timestamp = new Date().toLocaleString('fi-FI', {
				day: '2-digit',
				month: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
			});
			return `${gameTypeName} ${timestamp}`;
		}
	}

	/**
	 * Handle successful completion of temporary job
	 * @param tempJob Temporary job
	 */
	private handleTempJobSuccess(tempJob: BatchJob): void {
		tempJob.status = 'completed';
		tempJob.completedAt = new Date();
		tempJob.cancellationSubject.complete();

		// Emit completion event
		this.onJobCompleted.emit(tempJob);
	}

	/**
	 * Handle temporary job error
	 * @param tempJob Temporary job
	 * @param error Error message
	 */
	private handleTempJobError(tempJob: BatchJob, error: string): void {
		tempJob.status = 'failed';
		tempJob.error = error;
		tempJob.completedAt = new Date();
		tempJob.cancellationSubject.complete();

		// Emit error event
		this.onJobFailed.emit(tempJob);
	}
}
